import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Lightweight, dependency-free SVG charts for the creator dashboard --
// deliberately not D3/Recharts/Chart.js. The data volumes here (6 trend
// points, a handful of distribution buckets) don't need a charting library;
// a library would add bundle weight and another dependency to this app's
// already-fragmented charting story (see 14_STATE_OF_THE_STACK.md) for no
// real benefit at this scale.

const fmtMoney = (cents, currency = 'EUR') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format((cents || 0) / 100);

export { fmtMoney };

// Module-level, not recreated per render -- also lets useMemo's own
// dependency array stay accurate without depending on a fresh object
// literal that would otherwise defeat the memoization every render.
const CHART_WIDTH = 640;
const CHART_PADDING = { top: 16, right: 12, bottom: 24, left: 12 };

/* ---------- Revenue trend: simple area/line chart ---------- */
export const RevenueTrendChart = ({ trend = [], height = 180 }) => {
  const { t } = useTranslation();
  const [hover, setHover] = useState(null);
  const width = CHART_WIDTH;
  const padding = CHART_PADDING;
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const points = useMemo(() => {
    const vals = trend.map((t) => t.revenue_cents || 0);
    const max = Math.max(...vals, 1);
    const step = trend.length > 1 ? innerW / (trend.length - 1) : 0;
    return trend.map((t, i) => ({
      x: padding.left + i * step,
      y: padding.top + innerH - (t.revenue_cents / max) * innerH,
      ...t,
    }));
  }, [trend, innerW, innerH]);

  if (!trend.length) return null;

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${padding.top + innerH} L ${points[0].x} ${padding.top + innerH} Z`;

  return (
    <div className="relative w-full overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ minWidth: 480 }} preserveAspectRatio="none">
        <defs>
          <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#818cf8" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#818cf8" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#revenueFill)" />
        <path d={linePath} fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => (
          <g key={i}>
            <circle
              cx={p.x} cy={p.y} r={hover === i ? 6 : 4}
              fill="#6366f1" stroke="white" strokeWidth="2"
              className="cursor-pointer transition-all dark:stroke-gray-900"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
            <rect
              x={p.x - (innerW / trend.length) / 2} y={padding.top} width={innerW / trend.length} height={innerH}
              fill="transparent" className="cursor-pointer"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          </g>
        ))}
      </svg>
      {hover !== null && (
        <div
          className="absolute pointer-events-none bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-2.5 py-1.5 shadow-lg -translate-x-1/2 -translate-y-full"
          style={{ left: `${(points[hover].x / width) * 100}%`, top: `${(points[hover].y / height) * 100 - 4}%` }}
        >
          <div className="font-semibold">{fmtMoney(points[hover].revenue_cents)}</div>
          <div className="text-gray-300 dark:text-gray-400">{t('creator.charts.subscribersCount', { count: points[hover].subscriber_count })}</div>
        </div>
      )}
      <div className="flex justify-between mt-1 px-1 text-[10px] text-gray-400 dark:text-gray-500">
        <span>{trend[0]?.period_label}</span>
        <span>{trend[trend.length - 1]?.period_label}</span>
      </div>
    </div>
  );
};

/* ---------- Horizontal distribution bars (tiers, sectors, sentiment) ---------- */
const BAR_PALETTE = ['#818cf8', '#a78bfa', '#60a5fa', '#34d399', '#fbbf24', '#f472b6', '#22d3ee', '#fb923c'];

export const DistributionBars = ({ data = [], colors, emptyLabel = null }) => {
  const { t } = useTranslation();
  const entries = Object.entries(data || {});
  const total = entries.reduce((sum, [, v]) => sum + v, 0);

  if (!entries.length || total === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">{emptyLabel ?? t('creator.charts.noDataYet')}</p>;
  }

  const sorted = [...entries].sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-2.5">
      {sorted.map(([label, value], i) => {
        const pct = total > 0 ? (value / total) * 100 : 0;
        const color = (colors && colors[label]) || BAR_PALETTE[i % BAR_PALETTE.length];
        return (
          <div key={label}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-700 dark:text-gray-300 font-medium capitalize">{label}</span>
              <span className="text-gray-500 dark:text-gray-400">{t('creator.charts.valuePct', { value, pct: pct.toFixed(0) })}</span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700/50 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ---------- Small inline sparkline for per-group cards ---------- */
export const MiniSparkline = ({ values = [], width = 100, height = 28, color = '#818cf8' }) => {
  const { t } = useTranslation();
  if (!values.length || values.every((v) => v === 0)) {
    return <div className="text-[10px] text-gray-400 dark:text-gray-500">{t('creator.charts.noActivityYet')}</div>;
  }
  const max = Math.max(...values, 1);
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const points = values.map((v, i) => `${i * step},${height - (v / max) * height}`).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const SENTIMENT_COLORS = { bullish: '#34d399', bearish: '#f87171', neutral: '#94a3b8' };
export { SENTIMENT_COLORS };
